import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  CbrtRangeState,
  CompactTransaction,
  CurrentProfit,
  IndicatorConfig,
  IndicatorDataPoint,
  MrcState,
  RSIItem,
  Transaction,
  WakeupTimes,
} from '../../../../types';
import './index.css';
import {
  ComplexParamsBundle,
  LatestBotState,
} from '../../../../../Detail/types';
import { ComponentDimensions } from '../../../../../../types/Layout';
import TransactionCandles from '../../../../../Subscription/Content/Transactions/TransactionCandles';
import TransactionList from '../../../../../Subscription/Content/Transactions/TransactionList';
import { Candle } from 'grts-utils/types/Candle';
import {
  formatProfitTransactionsToGenericTransactions,
  transformToHeikinCandles,
} from '../../../../../../helpers/bundleTransactionsToCompactForm';
import { GenericTransactionInfo } from '../../../../../../types/Transaction';
import { Exchange } from 'grts-utils/types/Exchange';
import TransactionFilter from './TransactionFilter';

interface Props {
  rootDimensions: ComponentDimensions;
  transactions: Transaction[] | undefined;
  baseAsset: string;
  quoteAsset: string;
  exchange: Exchange;
  currentProfit: CurrentProfit | undefined;
  compactTransactionLength: number | undefined;
  allCompactTransactions: CompactTransaction[];
  currentComplexParams: ComplexParamsBundle | undefined;
  cbrtRangeStates: CbrtRangeState[];
  mrcStates: MrcState[];
  latestBotState: LatestBotState | undefined;
  wakeupTimes: WakeupTimes;
  indicatorConfig?: IndicatorConfig;
  candles: {
    higherT: Candle[] | undefined;
    higherTHeikin: Candle[] | undefined;
    shorterT: Candle[] | undefined;
    multiT: Candle[] | undefined;
  };
  useTakeProfitTransactions: boolean;
  rsiIndicatorValuesOppositeOrder: RSIItem[] | undefined;
  rsiIndicatorValuesSameOrder: RSIItem[] | undefined;
}

const Transactions = (props: Props) => {
  const {
    rootDimensions,
    transactions,
    baseAsset,
    quoteAsset,
    exchange,
    currentProfit,
    compactTransactionLength,
    currentComplexParams,
    cbrtRangeStates,
    mrcStates,
    latestBotState,
    indicatorConfig,
    candles,
    rsiIndicatorValuesOppositeOrder,
    rsiIndicatorValuesSameOrder,
    useTakeProfitTransactions,
  } = props;

  const [transactionTypeToggleValues, setTransactionTypeToggleValues] =
    useState({
      core: true,
      takeProfit: false,
      stopLoss: false,
    });

  const visibleCandles = useMemo(() => {
    return {
      normal: candles.higherT || [],
      heikin: candles.higherTHeikin || [],
    };
  }, [candles.higherT, candles.higherTHeikin]);

  const multiOrderCandles = useMemo(() => {
    return {
      normal: candles.multiT || [],
      heikin: transformToHeikinCandles(candles.multiT || []),
    };
  }, [candles.multiT]);

  const genericTransactions = useMemo(() => {
    if (!transactions) {
      return [];
    }

    return formatProfitTransactionsToGenericTransactions(
      transactions,
      baseAsset,
      quoteAsset,
      currentComplexParams?.complexParams,
      currentComplexParams?.timeFrames,
    );
  }, [
    baseAsset,
    currentComplexParams?.complexParams,
    currentComplexParams?.timeFrames,
    quoteAsset,
    transactions,
  ]);

  const bundledTransactions = useMemo(() => {
    if (!genericTransactions) {
      return {};
    }

    const bundledTransactions: Record<string, GenericTransactionInfo[]> = {};

    for (let i = 0; i < genericTransactions.length; i++) {
      const transaction = genericTransactions[i];
      const transactionTimestamp = transaction.transactionTimestamp;

      /** We need the multiOrder candles now */
      const candle = multiOrderCandles?.normal.find(
        (candle) =>
          candle.startTimestamp <= transactionTimestamp &&
          transactionTimestamp <= candle.endTimestamp,
      );
      const startTimestamp = candle?.startTimestamp.toString();

      if (!startTimestamp) {
        continue;
      }

      if (typeof bundledTransactions[startTimestamp] === 'undefined') {
        bundledTransactions[startTimestamp] = [transaction];
        continue;
      } else {
        bundledTransactions[startTimestamp].push(transaction);
      }
    }

    return bundledTransactions;
  }, [genericTransactions, multiOrderCandles?.normal]);

  if (!transactions) {
    return null;
  }

  return (
    <div
      className="result-transactions-root"
      style={{
        opacity: rootDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
        width: '97%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      {rootDimensions && (
        <>
          <TransactionCandles
            rootDimensions={rootDimensions}
            timeFrame={currentComplexParams?.timeFrames.higher}
            candles={visibleCandles}
            isLoading={false}
            multiOrderCandles={multiOrderCandles}
            bundledTransactions={bundledTransactions}
            genericTransactions={genericTransactions}
            baseAsset={baseAsset}
            quoteAsset={quoteAsset}
            exchange={exchange}
            transactionTypeToggleValues={transactionTypeToggleValues}
            rsiIndicatorValuesOppositeOrder={rsiIndicatorValuesOppositeOrder}
            rsiIndicatorValuesSameOrder={rsiIndicatorValuesSameOrder}
            indicatorConfig={indicatorConfig}
          />
          <TransactionFilter
            useTakeProfitTransactions={useTakeProfitTransactions}
            transactionTypeToggleValues={transactionTypeToggleValues}
            setTransactionTypeToggleValues={setTransactionTypeToggleValues}
          />
          <TransactionList
            transactions={genericTransactions}
            transactionsByTimestamp={bundledTransactions}
            isLoading={false}
            compactTransactionLength={compactTransactionLength}
            currentComplexParams={currentComplexParams}
            currentProfit={currentProfit?.currentProfit}
            currentPriceChange={currentProfit?.currentPriceChange}
            cbrtRangeStates={cbrtRangeStates}
            mrcStates={mrcStates}
            indicatorConfig={indicatorConfig}
            transactionTypeToggleValues={transactionTypeToggleValues}
            useTakeProfitTransactions={useTakeProfitTransactions}
            rsiIndicatorValuesOppositeOrder={rsiIndicatorValuesOppositeOrder}
            rsiIndicatorValuesSameOrder={rsiIndicatorValuesSameOrder}
            latestBotState={latestBotState}
          />
        </>
      )}
    </div>
  );
};

export default React.memo(Transactions);
